import { addImportantToStyles } from "@/lib/utils";
import { IUserData } from "@/types";

// Match #profile-background and related
const PROFILE_BG_REGEX =
    /#profile-background[^{]+\s*{(?:[^{}]*{[^{}]*})*[^{}]*}/g;

const USER_NAVIGATION_REGEX =
    /#user-navigation\b[^{]+\s*{(?:[^{}]*{[^{}]*})*[^{}]*}/g;

// Match ANY #root-profile selector (including pseudo-elements, classes, commas)
const ROOT_PROFILE_REGEX =
    /#root-profile[^{]*\{(?:[^{}]*\{[^{}]*\}[^{}]*|[^{}]*)*\}/g;

const KEYFRAMES_REGEX = /@keyframes\s+[^{]+\s*{(?:[^{}]*{[^{}]*})*[^{}]*}/g;

const IMPORT_FONT_REGEX = /@import\s+url\((['"]?)([^'")]+)\1\)\s*;?/gi;

const FONT_PROVIDERS = [
    "fonts.googleapis.com",
    "fonts.bunny.net",
    "use.typekit.net",
];

export default function ThemedStyles({
    theme,
    preview,
}: {
    theme: IUserData["chosen_theme"];
    preview?: boolean;
}) {
    if (!theme?.css?.trim()) return null;

    const { css } = theme;

    // Extract CSS parts
    let profileBackgroundCSS = css.match(PROFILE_BG_REGEX)?.join("") || "";
    const keyframesCSS = css.match(KEYFRAMES_REGEX)?.join("") || "";
    let userNavigationCSS = css.match(USER_NAVIGATION_REGEX)?.join("") || "";

    const ImportFontCSS =
        css
            .match(IMPORT_FONT_REGEX)
            ?.filter((rule: any) => {
                const urlMatch = rule.match(/\((['"]?)([^'")]+)\1\)/i);
                if (!urlMatch) return false;
                const url = urlMatch[2].trim().toLowerCase();
                // Block dangerous protocols
                if (url.startsWith("javascript:") || url.startsWith("data:")) {
                    return false;
                }
                // Allow known font providers
                if (FONT_PROVIDERS.some((provider) => url.includes(provider))) {
                    return true;
                }
                // Allow local font paths (e.g., /fonts/myfont.css)
                if (
                    /^\/fonts?\//i.test(url) ||
                    /\.(woff2?|ttf|otf|css)(\?|$)/i.test(url)
                ) {
                    return true;
                }
                return false;
            })
            .join("") || "";
    // Determine selectors based on preview mode
    const rootProfileSelector = preview
        ? `#root-profile-${theme.id}`
        : `#root-profile`;
    const profileBackgroundSelector = preview
        ? `#profile-background-${theme.id}`
        : `#profile-background`;
    const userNavigationSelector = preview
        ? `#user-navigation-${theme.id}`
        : `#user-navigation`;
    // Scope selectors if preview
    if (preview) {
        profileBackgroundCSS = profileBackgroundCSS.replace(
            /#profile-background\b/g,
            profileBackgroundSelector
        );
        userNavigationCSS = userNavigationCSS.replace(
            /#user-navigation\b/g,
            userNavigationSelector
        );
    }

    // Extract ALL user #root-profile related rules (including pseudo-elements)
    const userRootProfileRules =
        css
            .match(ROOT_PROFILE_REGEX)
            ?.map((block: any) =>
                preview
                    ? block.replace(/#root-profile\b/g, rootProfileSelector)
                    : block
            ) || [];

    // Remove keyframes, profile backgrounds, and imports from base CSS
    const cleanedCSS = css
        .replace(KEYFRAMES_REGEX, "")
        .replace(PROFILE_BG_REGEX, "")
        .replace(ROOT_PROFILE_REGEX, "")
        .replace(IMPORT_FONT_REGEX, "")
        .replace(USER_NAVIGATION_REGEX, "");

    // Merge user root-profile CSS with cleaned CSS into one block
    const mergedRootProfileCSS =
        userRootProfileRules.join("\n") +
        `\n${rootProfileSelector} { ${addImportantToStyles(cleanedCSS)} }`;

    // Helper to render only if CSS is not empty
    const renderStyle = (cssString: string) =>
        cssString.trim() ? (
            <style dangerouslySetInnerHTML={{ __html: cssString }} />
        ) : null;

    return (
        <>
            {/* KEY FRAMES */}
            {renderStyle(keyframesCSS)}

            {/* FONTS */}
            {renderStyle(ImportFontCSS)}

            {/* PROFILE BACKGROUND */}
            {renderStyle(addImportantToStyles(profileBackgroundCSS))}

            {/* USER NAVIGATION */}
            {renderStyle(addImportantToStyles(userNavigationCSS))}

            {/* MERGED ROOT PROFILE */}
            {renderStyle(mergedRootProfileCSS)}
        </>
    );
}
